import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import './Toast.css';

/**
 * Toast, Journal Party v1.2 "Paper + Ink" (Oct 7 2026, replaces the v7 side-bar
 * toasts). One dark card: Ink on paper in light mode, Surface 3 in dark mode,
 * cream type, no side bars or stock icons. A round mark pops in on the left:
 * a lime check for good news, a cyan "i" for information, an orange or yellow
 * "!" for problems. Errors stay until dismissed. No Party Animals: a random
 * animal per toast was in no approved design (Alex, Oct 22 2026).
 */

const toastVariants = cva(
  [
    'jp-toast pointer-events-auto grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3',
    'rounded-[20px] py-3 pl-3 pr-2',
    'bg-jp-ink text-jp-bg-page shadow-[0_14px_34px_-16px_rgba(11,16,32,0.55)]',
    'dark:bg-jp-bg-elevated dark:text-jp-fg-primary dark:ring-1 dark:ring-jp-line-edge',
  ],
  {
    variants: {
      variant: {
        success: '',
        info: '',
        warning: '',
        error: '',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  }
);

export interface ToastProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof toastVariants> {
  /** Toast title */
  title?: string;
  /** Toast description/message */
  description?: string;
  /** Back-compat alias for description */
  message?: string;
  /** Whether toast is open */
  open?: boolean;
  /** Callback when toast should close */
  onClose?: () => void;
  /** Back-compat alias for onClose */
  onDismiss?: () => void;
  /** Auto-dismiss duration in ms (0 = no auto-dismiss) */
  duration?: number;
  /** Action button */
  action?: React.ReactNode;
}

/** The leading mark: a check for good news, "i" for information, "!" for problems. */
export function ToastMark({ variant }: { variant?: ToastProps['variant'] }) {
  const tone =
    variant === 'error'
      ? 'bg-jp-accent-primary'
      : variant === 'warning'
        ? 'bg-jp-accent-yellow'
        : variant === 'success'
          ? 'bg-jp-accent-lime'
          : 'bg-jp-accent-secondary';
  return (
    <span
      aria-hidden="true"
      className={cn(
        'jp-toast-mark grid h-9 w-9 place-items-center rounded-full font-jp-display text-[18px] font-extrabold text-jp-ink',
        tone,
      )}
    >
      {variant === 'success' ? (
        <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12.5l4.5 4.5L19 7.5" />
        </svg>
      ) : variant === 'error' || variant === 'warning' ? (
        '!'
      ) : (
        'i'
      )}
    </span>
  );
}

export const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  (
    {
      className,
      variant,
      title,
      description,
      message,
      open = true,
      onClose,
      onDismiss,
      duration,
      action,
      children,
      ...props
    },
    ref
  ) => {
    const resolvedOnClose = onClose ?? onDismiss;
    const resolvedDescription = description ?? message;

    // Auto-dismiss logic
    React.useEffect(() => {
      if (open && duration && duration > 0 && resolvedOnClose) {
        const timer = setTimeout(resolvedOnClose, duration);
        return () => clearTimeout(timer);
      }
    }, [open, duration, resolvedOnClose]);

    if (!open) return null;

    return (
      <div
        ref={ref}
        role={variant === 'error' ? 'alert' : 'status'}
        aria-live={variant === 'error' ? 'assertive' : 'polite'}
        data-state={open ? 'open' : 'closed'}
        className={cn(toastVariants({ variant, className }))}
        {...props}
      >
        <ToastMark variant={variant} />

        <div className="min-w-0 py-0.5">
          {title && (
            <p className="font-jp-display text-[15px] font-bold leading-snug tracking-[-0.005em]">
              {title}
            </p>
          )}
          {resolvedDescription && (
            <p className="mt-0.5 font-jp-body text-[13.5px] leading-snug opacity-75">{resolvedDescription}</p>
          )}
          {children}
          {action && (
            <div className="mt-1.5 font-jp-body text-[13.5px] font-semibold text-jp-accent-primary [&_a]:no-underline [&_button]:no-underline">
              {action}
            </div>
          )}
        </div>

        {resolvedOnClose ? (
          <button
            type="button"
            onClick={resolvedOnClose}
            className="grid h-9 w-9 place-items-center self-start rounded-full opacity-60 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent-secondary"
            aria-label="Dismiss"
          >
            <XIcon className="h-4 w-4" />
          </button>
        ) : <span />}
      </div>
    );
  }
);

Toast.displayName = 'Toast';

// Toast container for positioning
export interface ToastContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Back-compat prop; positioning is handled internally */
  position?: string;
}

export const ToastContainer = React.forwardRef<HTMLDivElement, ToastContainerProps>(
  ({ className, children, position: _position, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'fixed z-50 pointer-events-none',
      // Phones: just above the bottom nav
      'left-4 right-4 bottom-[calc(var(--jp-mobile-nav-height,0px)+12px)]',
      // Desktop: top right
      'md:bottom-auto md:top-4 md:left-auto md:right-4 md:w-[380px]',
      'flex flex-col gap-2',
      '[&>*]:pointer-events-auto',
      className
    )}
    {...props}
  >
    {children}
  </div>
  )
);

ToastContainer.displayName = 'ToastContainer';

// Icons




const XIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
